import { Building2, Download, FileText, Upload, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../../services/api';

const ContractUpload = ({ onClose, onSuccess, selectedBuilding = null }) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState(selectedBuilding?.id || '');
  const queryClient = useQueryClient();

  // selectedBuilding prop이 변경될 때 selectedBuildingId 업데이트
  useEffect(() => {
    if (selectedBuilding?.id) {
      console.log('🔍 selectedBuilding prop 변경됨:', selectedBuilding);
      setSelectedBuildingId(String(selectedBuilding.id));
    }
  }, [selectedBuilding]);

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery(['user-info'], () => api.get('/auth/me'));

  // RMC 사용자 여부 확인
  const isRMCUser = userInfo?.role === 'rental_management_company' || 
                    userInfo?.role === 'rmc_admin' || 
                    userInfo?.role === 'rmc_staff';

  // 건물 목록 조회 (RMC 사용자는 RMC API 사용)
  const { data: buildings = [] } = useQuery(
    ['buildings-for-contract-upload', userInfo?.role],
    async () => {
      if (isRMCUser) {
        // RMC 사용자는 RMC 건물 API 사용
        const response = await api.get('/rmc/buildings');
        // RMC API는 {buildings: [...]} 형태로 응답
        if (response && response.buildings) {
          return response.buildings;
        } else if (Array.isArray(response)) {
          return response;
        }
        return [];
      } else {
        // 일반 사용자는 일반 건물 API 사용
        const response = await api.get('/buildings');
        if (Array.isArray(response)) return response;
        if (response?.data && Array.isArray(response.data)) return response.data;
        return [];
      }
    },
    {
      enabled: !!userInfo, // 사용자 정보가 로드된 후에만 실행
    }
  );

  // 템플릿 다운로드
  const downloadTemplate = async () => {
    if (!selectedBuildingId) {
      toast.error('건물을 선택해주세요.');
      return;
    }

    try {
      const response = await api.get(`/contracts/template?building_id=${selectedBuildingId}`, {
        responseType: 'blob',
      });
      
      console.log('다운로드 응답:', response);
      console.log('응답 데이터:', response.data);
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('템플릿이 다운로드되었습니다.');
    } catch (error) {
      console.error('템플릿 다운로드 오류:', error);
      toast.error('템플릿 다운로드를 실패했습니다.');
    }
  };

  // 파일 업로드
  const uploadMutation = useMutation(
    async ({ formData, buildingId }) => {
      console.log('🔍 업로드 mutation 시작');
      console.log('🔍 buildingId:', buildingId);
      console.log('🔍 formData:', formData);
      console.log('🔍 파일:', formData.get('file'));
      
      try {
        const response = await api.post(`/contracts/simple/upload?building_id=${buildingId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        console.log('🔍 업로드 응답:', response);
        console.log('🔍 응답 데이터:', response.data);
        console.log('🔍 응답 타입:', typeof response);
        // api.js의 인터셉터에서 FormData 업로드 응답은 원본 response를 반환하므로
        // response.data가 실제 데이터입니다
        return response.data || response;
      } catch (error) {
        console.error('🔍 업로드 mutation 에러:', error);
        console.error('🔍 에러 응답:', error.response);
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        console.log('업로드 성공 응답:', data);
        console.log('응답 데이터 타입:', typeof data);
        console.log('응답 데이터 키들:', Object.keys(data || {}));
        
        toast.success(
          `계약서 업로드가 완료되었습니다. 성공: ${data.success_count}건, 실패: ${data.error_count}건`
        );
        
        if (data.errors && data.errors.length > 0) {
          console.warn('업로드 오류:', data.errors);
          toast.warning(`${data.errors.length}건의 오류가 발생했습니다.`);
        }
        
        // 관련 데이터 새로고침
        queryClient.invalidateQueries(['contracts']);
        queryClient.invalidateQueries(['units']);
        queryClient.invalidateQueries(['tenants']);
        queryClient.invalidateQueries(['vacant-units']);
        
        onSuccess && onSuccess(data);
        onClose();
      },
      onError: (error) => {
        console.error('계약서 업로드 오류:', error);
        toast.error(
          error.response?.data?.error || '계약서 업로드 중 오류가 발생했습니다.'
        );
      },
    }
  );

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Excel 파일 확장자 확인
      const allowedExtensions = ['.xlsx', '.xls'];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error('Excel 파일(.xlsx, .xls)만 업로드 가능합니다.');
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    console.log('🔍 업로드 버튼 클릭됨');
    console.log('🔍 파일:', file);
    console.log('🔍 선택된 건물 ID:', selectedBuildingId);
    console.log('🔍 업로드 중 상태:', isUploading);
    
    if (!file) {
      console.log('❌ 파일이 없습니다');
      toast.error('파일을 선택해주세요.');
      return;
    }

    if (!selectedBuildingId) {
      console.log('❌ 건물이 선택되지 않았습니다');
      toast.error('건물을 선택해주세요.');
      return;
    }

    console.log('✅ 업로드 시작');
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      console.log('🔍 FormData 생성 완료, 파일명:', file.name);
      console.log('🔍 업로드 URL:', `/contracts/simple/upload?building_id=${selectedBuildingId}`);
      
      await uploadMutation.mutateAsync({ formData, buildingId: selectedBuildingId });
      console.log('✅ 업로드 mutation 완료');
    } catch (error) {
      console.error('❌ 업로드 에러:', error);
      // 에러는 mutation의 onError에서 처리됨
    } finally {
      setIsUploading(false);
      console.log('🔍 업로드 상태 해제');
    }
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">계약서 일괄 업로드</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 건물 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              건물 선택
            </label>
            <select
              value={selectedBuildingId}
              onChange={(e) => setSelectedBuildingId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">건물을 선택하세요</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name} ({building.address})
                </option>
              ))}
            </select>
          </div>

          {/* 템플릿 다운로드 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-900">
                  템플릿 다운로드
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  선택한 건물의 세대 정보가 포함된 Excel 템플릿을 다운로드하세요.
                </p>
              </div>
              <button
                onClick={downloadTemplate}
                disabled={!selectedBuildingId}
                className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                다운로드
              </button>
            </div>
          </div>

          {/* 파일 업로드 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excel 파일 선택
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 transition-colors">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>파일 선택</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">또는 드래그 앤 드롭</p>
                </div>
                <p className="text-xs text-gray-500">
                  Excel 파일(.xlsx, .xls)만 업로드 가능
                </p>
              </div>
            </div>
            
            {file && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-700">{file.name}</span>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 업로드 버튼 */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              취소
            </button>
            <button
              onClick={(e) => {
                console.log('🔍 버튼 클릭 이벤트 발생');
                console.log('🔍 버튼 disabled 상태:', !file || !selectedBuildingId || isUploading);
                console.log('🔍 file:', file);
                console.log('🔍 selectedBuildingId:', selectedBuildingId);
                console.log('🔍 isUploading:', isUploading);
                e.preventDefault();
                e.stopPropagation();
                handleUpload();
              }}
              disabled={!file || !selectedBuildingId || isUploading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? '업로드 중...' : '업로드'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractUpload;
